import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Input, Select, Space, Table, Tag } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const healthColor: Record<string, string> = {
  CRITICAL: 'red',
  POOR: 'volcano',
  DEGRADED: 'orange',
  WATCH: 'gold',
  HEALTHY: 'green',
  UNKNOWN: 'default',
};

const PmvReliabilityRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [q, setQ] = useState('');
  const [health, setHealth] = useState(sp.get('health_state') || undefined);
  const [page, setPage] = useState(1);
  const limit = 50;

  const query = useQuery({
    queryKey: ['pmv-reliability-register', q, health, page],
    queryFn: () =>
      pmvService.getReliabilityRegister({
        q: q || undefined,
        health_state: health,
        limit,
        offset: (page - 1) * limit,
      }),
  });

  const items = query.data?.items || [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Equipment Health Register"
        subtitle="Server-paginated reliability projections — UNKNOWN when evidence is insufficient"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/reliability/control-tower')}>
              Control Tower
            </Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/attention')}>Attention</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/failures')}>Failures</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/forecast')}>Forecast</Button>
          </Space>
        }
        extra={
          <Space wrap>
            <Input.Search
              placeholder="Search equipment"
              allowClear
              onSearch={(v) => {
                setQ(v);
                setPage(1);
              }}
              style={{ width: 240 }}
            />
            <Select
              allowClear
              placeholder="Health"
              style={{ width: 160 }}
              value={health}
              onChange={(v) => {
                setHealth(v);
                setPage(1);
              }}
              options={['CRITICAL', 'POOR', 'DEGRADED', 'WATCH', 'HEALTHY', 'UNKNOWN'].map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Missing evidence stays UNKNOWN — never shown as HEALTHY. Reliability observes; Maintenance owns cases."
      />
      {!query.isLoading && items.length === 0 ? (
        <Empty description="No equipment matches these health filters" />
      ) : (
        <Table
          rowKey="pmv_profile_id"
          size="small"
          loading={query.isLoading}
          dataSource={items}
          pagination={{
            current: page,
            pageSize: limit,
            total: query.data?.total || 0,
            onChange: setPage,
            showSizeChanger: false,
          }}
          scroll={{ x: 1100 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Equipment',
              dataIndex: 'equipment_label',
              width: 220,
              ellipsis: true,
              render: (v, r) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}
                  >
                    {v || r.asset_code || 'Equipment'}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Criticality',
              dataIndex: 'criticality',
              width: 110,
              render: (v) => v || '—',
            },
            {
              title: 'Health',
              dataIndex: 'health_state',
              width: 120,
              render: (v) => <Tag color={healthColor[v] || 'default'}>{v || 'UNKNOWN'}</Tag>,
            },
            {
              title: 'Confidence',
              dataIndex: 'health_confidence',
              width: 110,
            },
            {
              title: 'Primary driver',
              dataIndex: 'primary_driver',
              ellipsis: true,
              render: (v) => v || '—',
            },
            {
              title: 'MTBF (h)',
              dataIndex: 'mtbf_hours',
              width: 100,
              render: (v) => (v != null ? Number(v).toFixed(1) : '—'),
            },
            {
              title: 'Risk',
              dataIndex: 'risk_band',
              width: 100,
              render: (v) => v || '—',
            },
            {
              title: 'Attention',
              dataIndex: 'open_reliability_attention',
              width: 100,
              render: (v) =>
                v > 0 ? <Tag color="orange">{v}</Tag> : (v ?? 0),
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvReliabilityRegisterPage;
